import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Chip,
  IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import { format } from "date-fns";
import ImagePreviewModal from "./dialogs/imagepreviewmodal";
import {
  getPlantInitial,
  getPlantAvatarColor,
  PlantAvatar,
  themeColors,
  commonPaperStyles,
} from "../../../utils/content";

const PlantTable = ({
  plants,
  loading,
  onActionMenuOpen,
  activeTab,
  formatDate: formatDateProp,
}) => {
  const formatDate =
    formatDateProp ||
    ((dateValue) => {
      if (!dateValue) return "";
      try {
        const date =
          typeof dateValue === "number"
            ? new Date(dateValue * 1000)
            : new Date(dateValue);
        return format(date, "dd-MMM-yyyy");
      } catch (error) {
        return dateValue.toString();
      }
    });

  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [currentDocIndex, setCurrentDocIndex] = useState(0);
  const [previewTitle, setPreviewTitle] = useState("");

  const handleViewDocs = (plant) => {
    const documents = plant.documents || plant.document || [];

    if (documents.length === 0) {
      console.warn("No documents available");
      return;
    }

    const formattedDocs = documents.map((doc, index) => ({
      id: doc.id || doc.upload_id || `doc-${index}`,
      upload_id: doc.upload_id || doc.id,
      name: doc.name || `Document ${index + 1}`,
      type: doc.type || 0,
    }));

    setSelectedDocs(formattedDocs);
    setCurrentDocIndex(0);
    setPreviewTitle(
      `${plant.nickname || plant.name || plant.plant_id || "Plant"}'s Documents`
    );
    setPreviewModalOpen(true);
  };

  const handleDocumentNavigation = (direction) => {
    if (selectedDocs.length <= 1) return;

    let newIndex;
    if (direction === "next") {
      newIndex = (currentDocIndex + 1) % selectedDocs.length;
    } else {
      newIndex =
        (currentDocIndex - 1 + selectedDocs.length) % selectedDocs.length;
    }
    setCurrentDocIndex(newIndex);
  };

  const handleCloseModal = () => {
    setPreviewModalOpen(false);
    setSelectedDocs([]);
    setCurrentDocIndex(0);
    setPreviewTitle("");
  };

  useEffect(() => {
    if (plants && plants.length > 0) {
      plants.forEach((plant) => {
        if (!plant.avatar_color) {
          plant.avatar_color = getPlantAvatarColor(plant.plant_type);
        }
        if (!plant.initials) {
          plant.initials = getPlantInitial(plant.nickname || plant.name);
        }
      });
    }
  }, [plants]);

  const columns = [
    {
      field: "name",
      headerName: "Name & Plant ID",
      flex: 1,
      minWidth: 180,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <PlantAvatar plant={params.row} size={40} />
          <Box>
            <Typography variant="subtitle2" fontWeight={600}>
              {params.row.nickname ||
                params.row.name ||
                params.row.plant_id ||
                "Unnamed Plant"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              ID: {params.row.plant_id || "No ID"}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: "plant_type",
      headerName: "Plant Type",
      flex: 1,
      minWidth: 140,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Typography variant="body2">{params.value || "-"}</Typography>
      ),
    },
    {
      field: "make_model",
      headerName: "Make & Model",
      flex: 1,
      minWidth: 180,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {params.row.make || "—"}
          </Typography>
          {params.row.model && (
            <Typography variant="body2" color="textSecondary">
              {params.row.model}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "date",
      headerName: "Date on Site",
      flex: 1,
      minWidth: 140,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? formatDate(params.value) : "-"}
        </Typography>
      ),
    },
    {
      field: "registration_number",
      headerName: "Registration Number",
      flex: 1,
      minWidth: 180,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Typography variant="body2">{params.value || "-"}</Typography>
      ),
    },
    {
      field: "docs",
      headerName: "Docs",
      flex: 1,
      minWidth: 140,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const documents = params.row.documents || params.row.document || [];
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              height: "100%",
              gap: 0.5,
            }}
          >
            {documents.length > 0 ? (
              <>
                <Chip
                  label={`${documents.length} doc${
                    documents.length !== 1 ? "s" : ""
                  }`}
                  size="small"
                  variant="outlined"
                  sx={{ mb: 0 }}
                />
                <Typography
                  variant="caption"
                  component="a"
                  onClick={() => handleViewDocs(params.row)}
                  color="primary"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                    lineHeight: 1.2,
                  }}
                >
                  View Docs
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="textSecondary">
                —
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => onActionMenuOpen(e, params.row.id)}
          sx={{
            color: themeColors.primary,
            "&:hover": {
              bgcolor: `rgba(241, 98, 83, 0.08)`,
            },
          }}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  if (loading) {
    return (
      <Paper
        {...commonPaperStyles}
        sx={{ mx: 2, borderRadius: 2, overflow: "hidden" }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "71vh",
          }}
        >
          <CircularProgress sx={{ color: themeColors.primary }} />
        </Box>
      </Paper>
    );
  }

  if (!plants || plants.length === 0) {
    return (
      <Paper {...commonPaperStyles}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            p: 5,
            height: 400,
          }}
        >
          <Box
            component="img"
            src="/EmptyStat.svg"
            sx={{ width: 150, height: 150, opacity: 0.7, mb: 3 }}
            alt="No data"
          />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No Plants Found
          </Typography>
          <Typography variant="body2" color="textSecondary" align="center">
            {activeTab === 0
              ? "Use the Import Plant button to add plants to your project"
              : "Use the Add Plant button to create a new plant"}
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Box sx={{ height: "71vh", width: "100%", backgroundColor: "#ffffff" }}>
      <DataGrid
        rows={plants}
        columns={columns}
        getRowId={(row) => row.id || `plant-${row.index}`}
        loading={loading}
        disableSelectionOnClick
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row"
        }
        disableRowSelectionOnClick
        sx={{
          "& .MuiDataGrid-columnSeparator": {
            color: "#fff",
          },
          "& .MuiDataGrid-columnHeader": {
            fontWeight: 600,
            backgroundColor: "#f16253",
            color: "#ffffff",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 600,
            width: "100%",
          },
        }}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
      />

      {selectedDocs.length > 0 && (
        <ImagePreviewModal
          open={previewModalOpen}
          onClose={handleCloseModal}
          fileId={
            selectedDocs[currentDocIndex]?.upload_id ||
            selectedDocs[currentDocIndex]?.id
          }
          fileName={selectedDocs[currentDocIndex]?.name || "Document"}
          isOrg={true}
          documents={selectedDocs}
          currentIndex={currentDocIndex}
          onNavigate={handleDocumentNavigation}
          title={previewTitle}
        />
      )}
    </Box>
  );
};

export default PlantTable;
